import { useEffect, useRef } from "react";

interface UseKeyboardShortcutsProps {
  zenModeEnabled: boolean;
  showKeyboardShortcuts: boolean;
  setShowKeyboardShortcuts: (show: boolean) => void;
  setZenModeEnabled: (enabled: boolean) => void;
  // When any modal is open, ignore shortcuts
  isAnyModalOpen?: boolean;
  handleToggle: () => void;
  handleReset: () => void;
  handleMuteToggle: () => void;
  handleLoopToggle?: () => void;
  handleBPMChange: (bpm: number) => void;
  handleBeatsPerBarChange: (beats: number) => void;
  handleNoteValueChange: (noteValue: number) => void;
  bpm: number;
  beatsPerBar: number;
  noteValue: number;
  // Custom song structure state
  customSongStructureOn?: boolean;
  setCustomSongStructureOn?: (enabled: boolean) => void;
  // Subdivisions state
  subdivisionsEnabled?: boolean;
  handleSubdivisionsToggle?: () => void;
  // Progressive Tempo state
  progressiveTempoEnabled?: boolean;
  // Visual options toggles
  handleStressFirstBeatToggle?: () => void;
  handleDialFlashingToggle?: () => void;
  handleScreenFlashingToggle?: () => void;
  // Timer toggle
  handleTimerToggle?: () => void;
  // Bar navigation
  handleNextBar?: () => void;
  handlePreviousBar?: () => void;
  // Click style cycling
  handleClickStyleCycle?: () => void;
  // Progressive Tempo toggle
  handleProgressiveTempoToggle?: () => void;
}

export function useKeyboardShortcuts({
  zenModeEnabled,
  showKeyboardShortcuts,
  setShowKeyboardShortcuts,
  setZenModeEnabled,
  isAnyModalOpen = false,
  handleToggle,
  handleReset,
  handleMuteToggle,
  handleLoopToggle,
  handleBPMChange,
  handleBeatsPerBarChange,
  handleNoteValueChange,
  bpm,
  beatsPerBar,
  noteValue,
  customSongStructureOn = false,
  setCustomSongStructureOn,
  subdivisionsEnabled = false,
  handleSubdivisionsToggle,
  progressiveTempoEnabled = false,
  handleStressFirstBeatToggle,
  handleDialFlashingToggle,
  handleScreenFlashingToggle,
  handleTimerToggle,
  handleNextBar,
  handlePreviousBar,
  handleClickStyleCycle,
  handleProgressiveTempoToggle,
}: UseKeyboardShortcutsProps) {
  // Use refs to get current values in event handlers
  const bpmRef = useRef(bpm);
  const beatsPerBarRef = useRef(beatsPerBar);
  const noteValueRef = useRef(noteValue);

  bpmRef.current = bpm;
  beatsPerBarRef.current = beatsPerBar;
  noteValueRef.current = noteValue;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isAnyModalOpen) {
        // Allow closing behavior for the Keyboard Shortcuts modal specifically
        if (showKeyboardShortcuts) {
          if (event.key === "Escape") {
            setShowKeyboardShortcuts(false);
            return;
          }
          if (event.key === "Tab") {
            event.preventDefault();
            setShowKeyboardShortcuts(false);
            return;
          }
        }
        return;
      }
      // Check if user is focused on an input element
      const activeElement = document.activeElement;
      const isInputElement =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          (activeElement instanceof HTMLElement &&
            activeElement.contentEditable === "true"));

      // Only handle keys if not focused on an input element
      if (isInputElement) return;

      if (event.key === "Escape" && showKeyboardShortcuts) {
        setShowKeyboardShortcuts(false);
      }

      // Space key for start/stop
      if (event.key === " " && !event.repeat) {
        event.preventDefault(); // Prevent page scrolling
        handleToggle();
      }

      // R key for reset/restart bar (only when custom song structure is enabled)
      if ((event.key === "r" || event.key === "R") && customSongStructureOn) {
        event.preventDefault();
        handleReset();
      }

      // M key for mute/unmute
      if (event.key === "m" || event.key === "M") {
        event.preventDefault();
        handleMuteToggle();
      }

      // L key to toggle Loop (only when custom song structure is enabled)
      if ((event.key === "l" || event.key === "L") && customSongStructureOn) {
        if (handleLoopToggle) {
          event.preventDefault();
          handleLoopToggle();
        }
      }

      // Left/Right arrow keys for bar navigation (only when custom song structure is enabled)
      if (
        (event.key === "ArrowLeft" || event.key === "ArrowRight") &&
        !event.ctrlKey &&
        !event.shiftKey &&
        customSongStructureOn
      ) {
        event.preventDefault();

        if (event.key === "ArrowRight") {
          // Navigate to next bar
          if (handleNextBar) {
            handleNextBar();
          }
        } else if (event.key === "ArrowLeft") {
          // Navigate to previous bar
          if (handlePreviousBar) {
            handlePreviousBar();
          }
        }
      }

      // Arrow keys for BPM adjustment (only when Ctrl is NOT held and custom song structure is disabled)
      if (
        (event.key === "ArrowUp" || event.key === "ArrowDown") &&
        !event.ctrlKey &&
        !customSongStructureOn
      ) {
        event.preventDefault();

        if (event.key === "ArrowUp") {
          // Increase BPM by 1 (or 10 if Shift is held)
          const increment = event.shiftKey ? 10 : 1;
          const newBPM = bpmRef.current + increment;
          // Respect upper limit of 300 BPM
          if (newBPM <= 300) {
            handleBPMChange(newBPM);
          }
        } else if (event.key === "ArrowDown") {
          // Decrease BPM by 1 (or 10 if Shift is held)
          const decrement = event.shiftKey ? 10 : 1;
          const newBPM = bpmRef.current - decrement;
          // Respect lower limit of 20 BPM
          if (newBPM >= 20) {
            handleBPMChange(newBPM);
          }
        }
      }

      // Z key for Zen mode toggle
      if (event.key === "z" || event.key === "Z") {
        event.preventDefault();
        setZenModeEnabled(!zenModeEnabled);
      }

      // C key to toggle Custom Song Structure on/off (only if not disabled by subdivisions or progressive tempo)
      if (event.key === "c" || event.key === "C") {
        if (setCustomSongStructureOn && !subdivisionsEnabled && !progressiveTempoEnabled) {
          event.preventDefault();
          setCustomSongStructureOn(!customSongStructureOn);
        }
      }

      // S key to toggle Subdivisions (only when custom song structure is disabled)
      if (event.key === "s" || event.key === "S") {
        if (handleSubdivisionsToggle && !customSongStructureOn) {
          event.preventDefault();
          handleSubdivisionsToggle();
        }
      }

      // B key to toggle Stress Beat
      if (event.key === "b" || event.key === "B") {
        if (handleStressFirstBeatToggle) {
          event.preventDefault();
          handleStressFirstBeatToggle();
        }
      }

      // D key to toggle Flash Dial
      if (event.key === "d" || event.key === "D") {
        if (handleDialFlashingToggle) {
          event.preventDefault();
          handleDialFlashingToggle();
        }
      }

      // F key to toggle Flash Screen
      if (event.key === "f" || event.key === "F") {
        if (handleScreenFlashingToggle) {
          event.preventDefault();
          handleScreenFlashingToggle();
        }
      }

      // T key to toggle Timer
      if (event.key === "t" || event.key === "T") {
        if (handleTimerToggle) {
          event.preventDefault();
          handleTimerToggle();
        }
      }

      // X key to cycle through click styles
      if (event.key === "x" || event.key === "X") {
        if (handleClickStyleCycle) {
          event.preventDefault();
          handleClickStyleCycle();
        }
      }

      // P key to toggle Progressive Tempo (only when custom song structure is disabled)
      if ((event.key === "p" || event.key === "P") && !customSongStructureOn) {
        if (handleProgressiveTempoToggle) {
          event.preventDefault();
          handleProgressiveTempoToggle();
        }
      }

      // Ctrl + Arrow keys for beats per bar adjustment (only when custom song structure is disabled)
      if (
        (event.key === "ArrowUp" || event.key === "ArrowDown") &&
        event.ctrlKey &&
        !event.shiftKey &&
        !customSongStructureOn
      ) {
        event.preventDefault();

        if (event.key === "ArrowUp") {
          // Increase beats per bar by 1
          const newBeatsPerBar = beatsPerBarRef.current + 1;
          // Respect upper limit of 16 beats
          if (newBeatsPerBar <= 16) {
            handleBeatsPerBarChange(newBeatsPerBar);
          }
        } else if (event.key === "ArrowDown") {
          // Decrease beats per bar by 1
          const newBeatsPerBar = beatsPerBarRef.current - 1;
          // Respect lower limit of 2 beats
          if (newBeatsPerBar >= 2) {
            handleBeatsPerBarChange(newBeatsPerBar);
          }
        }
      }

      // Ctrl + Shift + Arrow keys for note value adjustment (only when custom song structure is disabled)
      if (
        (event.key === "ArrowUp" || event.key === "ArrowDown") &&
        event.ctrlKey &&
        event.shiftKey &&
        !customSongStructureOn
      ) {
        event.preventDefault();

        if (event.key === "ArrowUp") {
          // Increase note value (2 -> 4 -> 8 -> 16)
          const currentNoteValue = noteValueRef.current;
          let newNoteValue: number;

          if (currentNoteValue === 2) {
            newNoteValue = 4; // half note to quarter note
          } else if (currentNoteValue === 4) {
            newNoteValue = 8; // quarter note to eighth note
          } else if (currentNoteValue === 8) {
            newNoteValue = 16; // eighth note to sixteenth note
          } else {
            // Already at max (16), cycle back to min (2)
            newNoteValue = 2;
          }

          handleNoteValueChange(newNoteValue);
        } else if (event.key === "ArrowDown") {
          // Decrease note value (16 -> 8 -> 4 -> 2)
          const currentNoteValue = noteValueRef.current;
          let newNoteValue: number;

          if (currentNoteValue === 16) {
            newNoteValue = 8; // sixteenth note to eighth note
          } else if (currentNoteValue === 8) {
            newNoteValue = 4; // eighth note to quarter note
          } else if (currentNoteValue === 4) {
            newNoteValue = 2; // quarter note to half note
          } else {
            // Already at min (2), cycle back to max (16)
            newNoteValue = 16;
          }

          handleNoteValueChange(newNoteValue);
        }
      }

      // Tab key for opening/closing keyboard shortcuts
      if (event.key === "Tab") {
        event.preventDefault();
        if (showKeyboardShortcuts) {
          // If shortcuts are open, close them
          setShowKeyboardShortcuts(false);
        } else {
          // If shortcuts are closed, open them
          setShowKeyboardShortcuts(true);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    zenModeEnabled,
    showKeyboardShortcuts,
    setShowKeyboardShortcuts,
    setZenModeEnabled,
    isAnyModalOpen,
    handleToggle,
    handleReset,
    handleMuteToggle,
    handleBPMChange,
    handleBeatsPerBarChange,
    handleNoteValueChange,
    customSongStructureOn,
    subdivisionsEnabled,
    progressiveTempoEnabled,
    handleNextBar,
    handlePreviousBar,
    handleDialFlashingToggle,
    handleLoopToggle,
    handleScreenFlashingToggle,
    handleStressFirstBeatToggle,
    handleSubdivisionsToggle,
    setCustomSongStructureOn,
    handleTimerToggle,
    handleClickStyleCycle,
    handleProgressiveTempoToggle,
  ]);

  return { bpmRef, beatsPerBarRef, noteValueRef };
}
